/**
 * ReasoningCard — top-of-page intelligence briefing for an entity.
 * Fed by GET /api/reasoning/entity/{kind}/{slug}.
 *
 * States:
 *   • loading  → skeleton (header + narrative + 3 bullets)
 *   • empty    → subtle "Intelligence briefing pending · Next update …"
 *   • error    → same subtle empty state (never blocks the page)
 *   • ready    → narrative + why_now + what_to_watch + sources pills
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Lightbulb, Clock } from "lucide-react";
import { fetchers, qk } from "@/lib/queryClient";
import { Skeleton } from "@/components/ui/skeleton";

const T = {
  bg: "#F0F9FF",
  border: "#B8E1F3",
  navy: "#0F1B2D",
  mut: "#3D4B62",
  sub: "#5B6B82",
  accent: "#0FA9DB",
};

const CONF_STYLES = {
  high:   { bg: "#DCFCE7", fg: "#166534", label: "HIGH CONF" },
  medium: { bg: "#FEF3C7", fg: "#92400E", label: "MED CONF" },
  low:    { bg: "#F1F5F9", fg: "#64748B", label: "LOW CONF" },
};

function _relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const diffM = Math.round((Date.now() - d) / 60_000);
  if (diffM < 1) return "just now";
  if (diffM < 60) return `${diffM}m ago`;
  if (diffM < 60 * 24) return `${Math.round(diffM / 60)}h ago`;
  return `${Math.round(diffM / 60 / 24)}d ago`;
}

function ConfBadge({ conf }) {
  const s = CONF_STYLES[conf] || CONF_STYLES.low;
  return (
    <span
      data-testid={`reasoning-conf-${conf || "low"}`}
      style={{
        background: s.bg, color: s.fg, fontSize: 10, fontWeight: 700,
        letterSpacing: "0.06em", padding: "3px 8px", borderRadius: 999,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}
    >
      {s.label}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div
      data-testid="reasoning-card-loading"
      style={{
        background: T.bg, border: `1px solid ${T.border}`, borderRadius: 16,
        padding: "28px 32px",
        boxShadow: "0 4px 12px rgba(15,169,219,0.06)",
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <Skeleton className="h-5 w-5 rounded-full" />
        <Skeleton className="h-5 w-56" />
      </div>
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-11/12 mb-2" />
      <Skeleton className="h-4 w-9/12 mb-6" />
      <Skeleton className="h-3 w-24 mb-3" />
      <Skeleton className="h-4 w-full mb-1.5" />
      <Skeleton className="h-4 w-10/12 mb-1.5" />
      <Skeleton className="h-4 w-8/12" />
    </div>
  );
}

function EmptyCard({ nextUpdateLabel }) {
  return (
    <div
      data-testid="reasoning-card-empty"
      style={{
        background: T.bg, border: `1px dashed ${T.border}`, borderRadius: 16,
        padding: "24px 32px",
      }}
      className="flex items-center gap-3"
    >
      <Clock className="h-5 w-5" style={{ color: T.accent }} />
      <div>
        <div style={{ color: T.navy, fontSize: 14, fontWeight: 600 }}>
          Intelligence briefing pending
        </div>
        <div style={{ color: T.sub, fontSize: 12, marginTop: 2 }}>
          Next update at {nextUpdateLabel}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ children }) {
  return (
    <div
      style={{
        color: T.accent, fontSize: 10, fontWeight: 700,
        letterSpacing: "0.16em", textTransform: "uppercase",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        marginBottom: 10, marginTop: 22,
      }}
    >
      {children}
    </div>
  );
}

function BulletList({ items, testid }) {
  return (
    <ul className="list-none pl-0 m-0" data-testid={testid}>
      {items.map((x, i) => (
        <li
          key={i}
          className="flex items-start gap-2.5"
          style={{
            color: T.navy, fontSize: 14, lineHeight: 1.55,
            marginBottom: 8,
          }}
        >
          <span
            aria-hidden
            style={{
              flexShrink: 0, width: 5, height: 5, borderRadius: 999,
              background: T.accent, marginTop: 8,
            }}
          />
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Format a source_used entry which can be a URL or a domain or a title.
 * Returns a compact display label.
 */
function _sourcePillLabel(raw) {
  if (!raw) return "";
  // Accepts either a URL string or an object {url, title, source_name}
  if (typeof raw === "object") {
    if (raw.source_name) return String(raw.source_name);
    if (raw.url) return _sourcePillLabel(raw.url);
    if (raw.title) return String(raw.title).slice(0, 40);
    return "?";
  }
  try {
    const u = new URL(raw);
    return u.hostname.replace(/^www\./, "");
  } catch {
    if (raw.length > 40) return raw.slice(0, 37) + "…";
    return raw;
  }
}

export default function ReasoningCard({ kind, slug }) {
  const nextUpdateLabel = useMemo(() => "07:00 Brisbane", []);

  const q = useQuery({
    queryKey: qk.reasoningEntity(kind, slug),
    queryFn: () => fetchers.reasoningEntity(kind, slug),
    enabled: Boolean(kind && slug),
    staleTime: 60_000,
    retry: false,
  });

  if (q.isPending) return <SkeletonCard />;

  // 404 or any error → subtle empty state, page keeps working
  if (q.isError || !q.data || !q.data.narrative) {
    return <EmptyCard nextUpdateLabel={nextUpdateLabel} />;
  }

  const d = q.data;
  const sources = Array.isArray(d.sources_used) ? d.sources_used : [];
  const whyNow = Array.isArray(d.why_now) ? d.why_now : [];
  const wtw = Array.isArray(d.what_to_watch) ? d.what_to_watch : [];
  const nArt = Number(d.articles_used_count || 0);
  const nWeb = Number(d.web_sources_count || 0);
  const needsReview = d.review_status === "needs_manual_review";

  // Special empty-state when no articles were indexed yet (RAG-consistent
  // rendering — Turn C1: never show a hallucinated narrative if N=0).
  if (nArt === 0 && nWeb === 0) {
    return (
      <section
        data-testid={`reasoning-card-${kind}-${slug}-awaiting`}
        style={{
          background: T.bg, border: `1px dashed ${T.border}`, borderRadius: 16,
          padding: "28px 32px",
        }}
      >
        <div className="flex items-center gap-2.5 mb-2">
          <Lightbulb className="h-5 w-5" style={{ color: T.accent }} strokeWidth={2} />
          <span style={{ color: T.navy, fontSize: 15, fontWeight: 700 }}>
            Intelligence briefing
          </span>
        </div>
        <p style={{ color: T.mut, fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Awaiting first mentions — this entity will populate as articles are
          ingested. Next refresh at {nextUpdateLabel}.
        </p>
      </section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
      data-testid={`reasoning-card-${kind}-${slug}`}
      style={{
        background: T.bg, border: `1px solid ${T.border}`, borderRadius: 16,
        padding: "28px 32px",
        boxShadow: "0 4px 12px rgba(15,169,219,0.06)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="flex items-center gap-2.5">
          <Lightbulb className="h-5 w-5" style={{ color: T.accent }} strokeWidth={2} />
          <span
            style={{
              color: T.navy, fontSize: 15, fontWeight: 700,
              letterSpacing: "-0.005em",
            }}
          >
            Intelligence briefing
          </span>
          {needsReview && (
            <span
              data-testid="reasoning-review-flag"
              style={{
                background: "#FEF3C7", color: "#92400E", fontSize: 10,
                fontWeight: 700, letterSpacing: "0.06em",
                padding: "3px 8px", borderRadius: 999,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
              title="This narrative failed the automated consistency check. Manual review recommended."
            >
              REVIEW NEEDED
            </span>
          )}
        </div>
        <ConfBadge conf={d.confidence} />
      </div>
      <div
        style={{
          color: T.sub, fontSize: 12, marginBottom: 20,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        Generated {_relTime(d.generated_at)} · via {d.generated_via || "baseline"}
      </div>

      {/* Narrative */}
      <p
        style={{
          color: T.navy, fontSize: 16, lineHeight: 1.6,
          margin: 0, fontWeight: 400,
        }}
        data-testid="reasoning-narrative"
      >
        {d.narrative}
      </p>

      {/* Why now */}
      {whyNow.length > 0 && (
        <>
          <SectionHead>Why now</SectionHead>
          <BulletList items={whyNow} testid="reasoning-why-now" />
        </>
      )}

      {/* What to watch */}
      {wtw.length > 0 && (
        <>
          <SectionHead>What to watch</SectionHead>
          <BulletList items={wtw} testid="reasoning-what-to-watch" />
        </>
      )}

      {/* Sources footer */}
      {(nArt > 0 || nWeb > 0 || sources.length > 0) && (
        <div
          style={{
            marginTop: 24, paddingTop: 16,
            borderTop: `1px dashed ${T.border}`,
          }}
        >
          <div
            style={{
              color: T.sub, fontSize: 11,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              letterSpacing: "0.04em",
              marginBottom: 8,
            }}
            data-testid="reasoning-footer-transparency"
          >
            Based on {nArt} article{nArt === 1 ? "" : "s"} indexed
            {nWeb > 0 && ` · ${nWeb} web source${nWeb === 1 ? "" : "s"}`}
            {" · Generated "}{_relTime(d.generated_at)}
          </div>
          {sources.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="reasoning-sources">
              {sources.slice(0, 10).map((s, i) => (
                <span
                  key={i}
                  title={typeof s === "string" ? s : (s?.url || "")}
                  style={{
                    background: "#FFFFFF", color: T.mut,
                    border: `1px solid ${T.border}`, borderRadius: 999,
                    fontSize: 11, padding: "2px 10px",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}
                >
                  {_sourcePillLabel(s)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.section>
  );
}
